import {
  useWebSocket,
  WebSocketResult,
  WebSocketStatus,
} from "@/composables/websocket";
import {
  WebsocketPayloadModel,
  WidgetConfigurationChannelInfo,
} from "@/types/inboundWebhookPayload";
import { useConversation } from "@/composables/conversation";
import { v4 as uuid } from "uuid";
import { useWidgetConfig } from "@/composables/widgetConfig";
import { useApp } from "@/composables/use-app";
import { computed, Ref, ref, watch } from "vue";
import { useWidgetTheme } from "@/composables/widgetTheme";

interface UseChatConnectionReturn {
  status: Ref<WebSocketStatus>;
  ws: Ref<WebSocket | undefined>;
  sendReply: (text: string) => void;
  sendFiles: (attachments: { fileName: string; fileId: string }[]) => void;
  requestStartConversation: (fields: { [p: string]: unknown }) => void;
  notifyCloseWidget: () => void;
  notifyOpenWidget: () => void;
  requestEndConversation: () => void;
  requestTransferConversation: (
    channel: WidgetConfigurationChannelInfo["channel"],
    address: string
  ) => void;
}

const {
  setInitialState,
  upsertMessage,
  insertPendingMessage,
  setStarted,
  setEnded,
  setTransferred,
  setVisitorInfo,
  openWidget,
  closeWidget,
  setIsReady,
} = useConversation();
const { setConfiguration } = useWidgetConfig();
const { widgetId, visitorId, setVisitorId, isPreviewMode } = useApp();
const { theme, setTheme } = useWidgetTheme();

const pingInterval = 60_000;

if (!process.env.VUE_APP_WEBSOCKET_URI) {
  throw new Error("Websocket URL missing from config");
}

const wsUrl = computed(() => {
  return visitorId.value
    ? `${process.env.VUE_APP_WEBSOCKET_URI}?visitorId=${visitorId.value}&widgetId=${widgetId.value}`
    : `${process.env.VUE_APP_WEBSOCKET_URI}?widgetId=${widgetId.value}`;
});

let wsWrapper: WebSocketResult<WebsocketPayloadModel> | undefined;

export function useChatConnection(): UseChatConnectionReturn {
  if (isPreviewMode.value) {
    console.debug("Skipping chat connection in preview mode");

    return {
      status: ref<WebSocketStatus>("CLOSED"),
      ws: ref(undefined),
      notifyCloseWidget: () => {},
      notifyOpenWidget: () => {},
      requestEndConversation: () => {},
      requestStartConversation: () => {},
      requestTransferConversation: () => {},
      sendFiles: (attachments: { fileName: string; fileId: string }[]) => {},
      sendReply: (text: string) => {
        upsertMessage({
          visitorId: "",
          connectionId: "",
          conversationId: "",
          messageId: uuid(),
          timestamp: new Date().getTime().toString(),
          data: {
            text: text,
            eventType: "reply",
            authorType: "client",
            author: {
              name: undefined,
            },
          },
        });
      },
    } as UseChatConnectionReturn;
  }

  if (!wsWrapper) {
    wsWrapper = useWebSocket<WebsocketPayloadModel>(wsUrl.value, {
      autoReconnect: {
        delay: "exponential",
        retries: -1,
      },
      immediate: true,
      heartbeat: {
        interval: pingInterval,
        message: JSON.stringify({ op: "ping" }),
      },
      onMessage: (msg, e, data) => {
        switch (data.op) {
          case "init": {
            if (!data.success) {
              console.warn("Backend reported non-success 'init'");
              // TODO show error to client
              break;
            }

            if (!data.init) {
              console.warn("Got 'init' without a init payload");
              break;
            }

            setInitialState(data.init);
            setConfiguration(data.init.widgetConfiguration);
            setVisitorId(data.init.visitorId);

            if (data.init.widgetConfiguration.theme) {
              setTheme(data.init.widgetConfiguration.theme);
            }

            setIsReady();

            break;
          }

          case "pong":
            break;

          case "reply": {
            if (!data.success) {
              console.warn("Backend reported non-success 'reply'");
              // TODO show error to client
              break;
            }

            if (!data.reply) {
              console.warn("Got 'reply' without a reply payload");
              break;
            }

            upsertMessage(data.reply);
            break;
          }

          case "conversation_started": {
            if (!data.success) {
              console.warn(
                "Backend reported non-success 'conversation_started'"
              );
              // TODO show error to client
              break;
            }

            if (!data.conversation?.id) {
              console.warn("Got 'conversation_started' without an ID");
              // TODO show error to client
              break;
            }

            setStarted(data.conversation?.id);

            if (data.visitorInfo) {
              setVisitorInfo(data.visitorInfo);
            }

            break;
          }

          case "conversation_ended": {
            if (!data.success) {
              console.warn("Backend reported non-success 'conversation_ended'");
              // TODO show error to client
              break;
            }

            if (data.reply) {
              upsertMessage(data.reply);
            }

            setEnded();
            break;
          }

          case "conversation_transferred": {
            if (!data.success) {
              console.warn(
                "Backend reported non-success 'conversation_transferred'"
              );
              // TODO show error to client
              break;
            }

            if (data.reply) {
              upsertMessage(data.reply);
            }

            setTransferred();
            break;
          }

          case "unknown": {
            console.error("Got unknown data", data);
            break;
          }
        }
      },
      onConnected: (ws) => {
        ws.send(JSON.stringify({ op: "init" }));
      },
      onDisconnected: () => {
        console.log("disconnected");
      },
      onError: (ws, err) => {
        console.warn("error", err);
      },
    });
  }

  const { status, ws } = wsWrapper;

  const sendMessage = (message: Record<string, any>) => {
    if (!ws.value) {
      throw new Error("websocket isn't initialized");
    }

    if (status.value !== "OPEN") {
      throw new Error(
        `Can't send message, unexpected connection status ${status.value}`
      );
    }

    ws.value?.send(JSON.stringify(message));
  };

  const sendReply = (text: string) => {
    const messageId = uuid();

    sendMessage({
      op: "reply",
      reply: {
        message: text,
        messageId: messageId,
      },
    });

    insertPendingMessage(messageId, text);
  };

  const sendFiles = (attachments: { fileName: string; fileId: string }[]) => {
    const messageId = uuid();

    sendMessage({
      op: "reply",
      reply: {
        messageId: messageId,
        attachments: attachments,
      },
    });

    //insertPendingMessage(messageId, text);
  };

  const notifyCloseWidget = () => {
    closeWidget();

    sendMessage({
      op: "close_widget",
    });
  };

  const notifyOpenWidget = () => {
    openWidget();

    sendMessage({
      op: "open_widget",
    });
  };

  const requestStartConversation = (fields: { [index: string]: unknown }) => {
    sendMessage({
      op: "new_conversation",
      new_conversation: fields
        ? {
            survey_data: fields,
          }
        : null,
    });
  };

  const requestEndConversation = () => {
    sendMessage({
      op: "end_conversation",
    });
  };

  const requestTransferConversation = (
    channel: WidgetConfigurationChannelInfo["channel"],
    address: string
  ) => {
    sendMessage({
      op: "transfer_conversation",
      transfer_conversation: {
        target_channel: channel,
        target_address: address,
      },
    });
  };

  return {
    status: wsWrapper.status,
    ws: wsWrapper.ws,
    sendReply,
    sendFiles,
    requestStartConversation,
    notifyCloseWidget,
    notifyOpenWidget,
    requestEndConversation,
    requestTransferConversation,
  };
}
